import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, FormControlLabel, Grid, IconButton, Switch, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import agent from '../../agent';
import Thumbnails from '../Thumbnails';
import UploadFiles from '../UploadFiles';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const FreakItemEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const translation = useTranslation();
  const context = useSelector((state) => state.common.context);

  const [loading, setLoading] = useState(!!slug);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(null);
  const [customCategory, setCustomCategory] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [form, setForm] = useState({
    name: '', brand: '', model: '', year: '', purchaseDate: '', body: '', isPublic: true,
  });
  const [specs, setSpecs] = useState([]);
  const [images, setImages] = useState([]);

  useEffect(() => {
    agent.FreakItems.categories(context).then((res) => setCategories(res.freakItemCategories || []));
    if (slug) {
      agent.FreakItems.get(slug).then((res) => {
        const item = res.freakItem;
        setForm({
          name: item.name,
          brand: item.brand || '',
          model: item.model || '',
          year: item.year || '',
          purchaseDate: item.purchaseDate ? item.purchaseDate.substring(0, 10) : '',
          body: item.body || '',
          isPublic: item.public,
        });
        setSpecs(item.specs || []);
        setImages(item.images || []);
        setCategoryId(item.category?.id || null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [slug, context]);

  const setField = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const handleImageUpload = (file) => {
    const newImage = {
      id: file?.data?.id,
      imageUrl: file?.data?.fileUrl,
      name: file?.data?.filename,
    };
    setImages((prevImages) => [...prevImages, newImage]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const freakItem = {
        name: form.name.trim(),
        context,
        body: form.body,
        description: form.body.replace(/<[^>]*>/g, '').substring(0, 200),
        brand: form.brand.trim(),
        model: form.model.trim(),
        year: form.year ? Number(form.year) : null,
        purchaseDate: form.purchaseDate || null,
        specs: specs.filter((spec) => spec.key.trim() && spec.value.trim()),
        images: images.map((image) => image.name),
        public: form.isPublic,
        ...(customMode ? { categoryName: customCategory.trim() } : { categoryId }),
      };
      if (slug) {
        await agent.FreakItems.update(slug, freakItem);
      } else {
        await agent.FreakItems.create(freakItem);
      }
      navigate(-1);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const saveDisabled = !form.name.trim() || (customMode && !customCategory.trim()) || saving;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {slug ? form.name : translation.freakItems.add}
      </Typography>

      <TextField fullWidth label={translation.freakItems.name} value={form.name} onChange={setField('name')} sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>{translation.freakItems.category}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {categories.map((category) => (
          <Chip
            key={category.id}
            label={category.name}
            color={!customMode && categoryId === category.id ? 'primary' : 'default'}
            onClick={() => { setCategoryId(category.id); setCustomMode(false); }}
          />
        ))}
        <Chip
          label={translation.freakItems.customCategory}
          color={customMode ? 'primary' : 'default'}
          variant="outlined"
          onClick={() => setCustomMode(true)}
        />
      </Box>
      {customMode && (
        <TextField fullWidth label={translation.freakItems.customCategory} value={customCategory}
          onChange={(event) => setCustomCategory(event.target.value)} sx={{ mb: 2 }} />
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label={translation.freakItems.brand} value={form.brand} onChange={setField('brand')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label={translation.freakItems.model} value={form.model} onChange={setField('model')} />
        </Grid>
        <Grid item xs={6} sm={6}>
          <TextField fullWidth type="number" label={translation.freakItems.year} value={form.year} onChange={setField('year')} />
        </Grid>
        <Grid item xs={6} sm={6}>
          <TextField fullWidth type="date" label={translation.freakItems.purchaseDate} value={form.purchaseDate}
            onChange={setField('purchaseDate')} InputLabelProps={{ shrink: true }} />
        </Grid>
      </Grid>

      <TextField fullWidth multiline minRows={4} label={translation.freakItems.description}
        value={form.body} onChange={setField('body')} sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>{translation.freakItems.specs}</Typography>
      {specs.map((spec, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField size="small" sx={{ flex: 1 }} placeholder="engine"
            value={spec.key}
            onChange={(event) => setSpecs(specs.map((s, i) => i === index ? { ...s, key: event.target.value } : s))} />
          <TextField size="small" sx={{ flex: 1 }} placeholder="450cc"
            value={spec.value}
            onChange={(event) => setSpecs(specs.map((s, i) => i === index ? { ...s, value: event.target.value } : s))} />
          <IconButton size="small" onClick={() => setSpecs(specs.filter((_, i) => i !== index))}><ClearIcon /></IconButton>
        </Box>
      ))}
      <Button startIcon={<AddIcon />} onClick={() => setSpecs([...specs, { key: '', value: '' }])} sx={{ mb: 2 }}>
        {translation.freakItems.addSpec}
      </Button>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>{translation.freakItems.title}</Typography>
      {images?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Thumbnails images={images} handleDeleteFn={setImages} />
        </Box>
      )}
      <UploadFiles onFileUpload={handleImageUpload} setSaveDisabled={setSaving} dest="freakItem" />

      <Box>
        <FormControlLabel
          control={<Switch checked={form.isPublic}
            onChange={(event) => setForm({ ...form, isPublic: event.target.checked })} />}
          label={translation.freakItems.public}
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={saveDisabled}
        onClick={handleSave}
        sx={{ mt: 2, bgcolor: DEFAULT_THEME_COLOR, minHeight: 48 }}
      >
        {translation.freakItems.save}
      </Button>
    </Box>
  );
};

export default FreakItemEditor;
